/**
 * PII (Personally Identifiable Information) Scrubber
 * Removes or anonymizes personal information from text before sending to AI
 */

// Common patterns for PII detection
const PII_PATTERNS = {
  // Email addresses
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/gi,
  
  // Phone numbers (various formats)
  phone: /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
  
  // Social Security Numbers (US format)
  ssn: /\b\d{3}-?\d{2}-?\d{4}\b/g,
  
  // Credit card numbers (basic pattern)
  creditCard: /\b\d{4}[-.\s]?\d{4}[-.\s]?\d{4}[-.\s]?\d{4}\b/g,
  
  // IP addresses
  ipAddress: /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g,
  
  // Dates that might indicate birthdates (MM/DD/YYYY, DD/MM/YYYY, etc.)
  date: /\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/g,
  
  // URLs
  url: /https?:\/\/[^\s]+/gi,
  
  // Common name patterns (capitalized words that might be names)
  // This is more conservative - only matches common name patterns
  potentialName: /\b[A-Z][a-z]+ [A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/g,
};

// Common first names database (top 100 most common)
const COMMON_FIRST_NAMES = new Set([
  'James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
  'William', 'Elizabeth', 'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Christopher', 'Karen', 'Charles', 'Nancy', 'Daniel', 'Lisa',
  'Matthew', 'Betty', 'Anthony', 'Margaret', 'Mark', 'Sandra', 'Donald', 'Ashley',
  'Steven', 'Kimberly', 'Andrew', 'Emily', 'Paul', 'Donna', 'Joshua', 'Michelle',
  'Kenneth', 'Dorothy', 'Kevin', 'Carol', 'Brian', 'Amanda', 'George', 'Melissa',
  'Edward', 'Deborah', 'Ronald', 'Stephanie', 'Timothy', 'Rebecca', 'Jason', 'Sharon',
  'Jeffrey', 'Laura', 'Ryan', 'Cynthia', 'Jacob', 'Kathleen', 'Gary', 'Amy',
  'Nicholas', 'Angela', 'Eric', 'Shirley', 'Jonathan', 'Anna', 'Stephen', 'Brenda',
  'Larry', 'Pamela', 'Justin', 'Emma', 'Scott', 'Nicole', 'Brandon', 'Helen',
  'Benjamin', 'Samantha', 'Samuel', 'Katherine', 'Frank', 'Christine', 'Gregory', 'Debra',
  'Raymond', 'Rachel', 'Alexander', 'Carolyn', 'Patrick', 'Janet', 'Jack', 'Virginia',
  'Dennis', 'Maria', 'Jerry', 'Heather', 'Tyler', 'Diane', 'Aaron', 'Julie',
  'Jose', 'Joyce', 'Adam', 'Victoria', 'Henry', 'Kelly', 'Nathan', 'Christina',
  'Zachary', 'Joan', 'Douglas', 'Evelyn', 'Kyle', 'Lauren', 'Noah', 'Judith',
  'Ethan', 'Megan', 'Jeremy', 'Cheryl', 'Walter', 'Andrea', 'Christian', 'Hannah',
  'Keith', 'Jacqueline', 'Roger', 'Martha', 'Terry', 'Gloria', 'Austin', 'Teresa',
  'Sean', 'Sara', 'Gerald', 'Janice', 'Carl', 'Marie', 'Harold', 'Julia',
  'Dylan', 'Grace', 'Arthur', 'Judy', 'Lawrence', 'Theresa', 'Jordan', 'Madison',
  'Jesse', 'Beverly', 'Bryan', 'Denise', 'Billy', 'Marilyn', 'Joe', 'Amber',
  'Bruce', 'Danielle', 'Gabriel', 'Rose', 'Logan', 'Brittany', 'Albert', 'Diana',
  'Willie', 'Abigail', 'Alan', 'Jane', 'Juan', 'Lori', 'Wayne', 'Olivia',
  'Elijah', 'Jean', 'Randy', 'Catherine', 'Roy', 'Frances', 'Vincent', 'Kathryn',
  'Ralph', 'Samantha', 'Eugene', 'Janet', 'Russell', 'Rachel', 'Louis', 'Carolyn',
  'Philip', 'Virginia', 'Bobby', 'Maria', 'Johnny', 'Heather', 'Willie', 'Diane'
]);

// Common last names database (top 50 most common)
const COMMON_LAST_NAMES = new Set([
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Wilson', 'Anderson', 'Thomas', 'Taylor',
  'Moore', 'Jackson', 'Martin', 'Lee', 'Thompson', 'White', 'Harris', 'Sanchez',
  'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker', 'Young', 'Allen', 'King',
  'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores', 'Green', 'Adams',
  'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell', 'Mitchell', 'Carter', 'Roberts'
]);

/**
 * Checks if a word is likely a name based on common name databases
 */
function isLikelyName(word) {
  const parts = word.trim().split(/\s+/);
  if (parts.length < 2) return false;
  
  const firstName = parts[0];
  const lastName = parts[parts.length - 1];
  
  return COMMON_FIRST_NAMES.has(firstName) || COMMON_LAST_NAMES.has(lastName);
}

/**
 * Scrubs PII from a single text string
 * @param {string} text - Text to scrub
 * @param {object} options - Scrubbing options
 * @returns {object} - { scrubbedText, replacements } where replacements is a map of original -> replacement
 */
export function scrubPII(text, options = {}) {
  if (!text || typeof text !== 'string') {
    return { scrubbedText: text, replacements: new Map() };
  }

  const {
    replaceWith = '[REDACTED]',
    preserveContext = true, // Keep some context like "Student [REDACTED]" instead of just "[REDACTED]"
    scrubNames = true,
    scrubEmails = true,
    scrubPhones = true,
    scrubSSNs = true,
    scrubCreditCards = true,
    scrubIPs = true,
    scrubDates = false, // Dates might be relevant for assignments
    scrubURLs = false // URLs might be relevant for references
  } = options;

  let scrubbedText = text;
  const replacements = new Map();
  let replacementCounter = 1;

  // Helper to create replacement
  const createReplacement = (original, type) => {
    const key = `[REDACTED_${type.toUpperCase()}_${replacementCounter++}]`;
    replacements.set(key, original);
    return preserveContext && type === 'NAME' 
      ? `[Student ${key}]` 
      : key;
  };

  // Scrub emails
  if (scrubEmails) {
    scrubbedText = scrubbedText.replace(PII_PATTERNS.email, (match) => {
      const replacement = createReplacement(match, 'EMAIL');
      return replacement;
    });
  }

  // Scrub phone numbers
  if (scrubPhones) {
    scrubbedText = scrubbedText.replace(PII_PATTERNS.phone, (match) => {
      const replacement = createReplacement(match, 'PHONE');
      return replacement;
    });
  }

  // Scrub SSNs
  if (scrubSSNs) {
    scrubbedText = scrubbedText.replace(PII_PATTERNS.ssn, (match) => {
      const replacement = createReplacement(match, 'SSN');
      return replacement;
    });
  }

  // Scrub credit cards
  if (scrubCreditCards) {
    scrubbedText = scrubbedText.replace(PII_PATTERNS.creditCard, (match) => {
      const replacement = createReplacement(match, 'CARD');
      return replacement;
    });
  }

  // Scrub IP addresses
  if (scrubIPs) {
    scrubbedText = scrubbedText.replace(PII_PATTERNS.ipAddress, (match) => {
      const replacement = createReplacement(match, 'IP');
      return replacement;
    });
  }

  // Scrub dates (optional)
  if (scrubDates) {
    scrubbedText = scrubbedText.replace(PII_PATTERNS.date, (match) => {
      const replacement = createReplacement(match, 'DATE');
      return replacement;
    });
  }

  // Scrub URLs (optional)
  if (scrubURLs) {
    scrubbedText = scrubbedText.replace(PII_PATTERNS.url, (match) => {
      const replacement = createReplacement(match, 'URL');
      return replacement;
    });
  }

  // Scrub names (more complex - check against name database)
  if (scrubNames) {
    scrubbedText = scrubbedText.replace(PII_PATTERNS.potentialName, (match) => {
      // Only scrub if it's likely a name
      if (isLikelyName(match)) {
        const replacement = createReplacement(match, 'NAME');
        return replacement;
      }
      return match; // Keep it if not likely a name
    });
  }

  return { scrubbedText, replacements };
}

/**
 * Scrubs PII from an object (recursively processes strings)
 * @param {object} obj - Object to scrub
 * @param {object} options - Scrubbing options
 * @returns {object} - { scrubbedObject, replacements }
 */
export function scrubPIIFromObject(obj, options = {}) {
  if (!obj) return { scrubbedObject: obj, replacements: new Map() };

  const allReplacements = new Map();
  let scrubbedObject;

  if (typeof obj === 'string') {
    const result = scrubPII(obj, options);
    return {
      scrubbedObject: result.scrubbedText,
      replacements: result.replacements
    };
  }

  if (Array.isArray(obj)) {
    scrubbedObject = obj.map(item => {
      const result = scrubPIIFromObject(item, options);
      result.replacements.forEach((value, key) => allReplacements.set(key, value));
      return result.scrubbedObject;
    });
  } else if (typeof obj === 'object') {
    scrubbedObject = {};
    for (const [key, value] of Object.entries(obj)) {
      // Skip certain keys that might contain names but are metadata
      if (key === 'studentName' || key === 'studentId' || key === 'name') {
        // These are explicitly student identifiers - always scrub
        if (typeof value === 'string') {
          const result = scrubPII(value, { ...options, scrubNames: true });
          result.replacements.forEach((val, k) => allReplacements.set(k, val));
          scrubbedObject[key] = result.scrubbedText;
        } else {
          scrubbedObject[key] = value;
        }
      } else {
        const result = scrubPIIFromObject(value, options);
        result.replacements.forEach((val, k) => allReplacements.set(k, val));
        scrubbedObject[key] = result.scrubbedObject;
      }
    }
  } else {
    scrubbedObject = obj;
  }

  return { scrubbedObject, replacements: allReplacements };
}

/**
 * Restores original PII in text using replacements map
 * @param {string} text - Text with placeholders
 * @param {Map} replacements - Map of placeholder -> original value
 * @returns {string} - Text with restored values
 */
export function restorePII(text, replacements) {
  if (!text || !replacements) return text;
  
  let restored = text;
  replacements.forEach((original, placeholder) => {
    restored = restored.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), original);
  });
  
  return restored;
}
